<script setup lang="ts">
// Public release-notes page (RDT-286): what changed for customers, newest month first.
//
// Grouped by the month a change reached customers, not by version — releases are frequent and
// per-app (dozens of versions a month, one change often spanning several), so versions would be
// noise. Each month leads with new features; fixes sit in a collapsed panel so the page informs
// without overwhelming.
//
// Carries the regular MainMenu (RDT-429), so a visitor reaches the rest of the app from here like from
// any other page; the menu works the same for anonymous and logged-in visitors.
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { ProfileAdministrationAPI } from "../api";
import { useLocaleFromQuery } from "../composables/useLocaleFromQuery.ts";
import {
    CUSTOMER_RELEASE_NOTES_FLAG,
    useFeatureFlag,
    useFlagsResolved,
} from "../composables/useFeatureFlag.ts";
import { i18n, trainerDetails } from "../main.ts";
import TitleHeadline from "../components/common/TitleHeadline.vue";
import type { ReleaseNote, ReleaseNoteMonth } from "../types/PersologOnlineTypes.ts";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const trainer = trainerDetails;

// Off for this environment ⇒ the URL behaves as if the page did not exist: no fetch, straight home.
const enabled = useFeatureFlag(CUSTOMER_RELEASE_NOTES_FLAG);
const flagsResolved = useFlagsResolved();

const loading = ref<boolean>(false);
const error = ref<string>("");
const months = ref<ReleaseNoteMonth[]>([]);

/**
 * Which accordion panels are open, per month. New features start expanded and fixes collapsed, so
 * the page leads with what is new while keeping everything reachable. Per month rather than shared,
 * so opening one month's fixes does not expand every other month too.
 */
const openPanels = ref<Record<string, string[]>>({});

const activeLocale = computed(() => i18n.global.locale.value);

/**
 * Notes are written in German and English only. German readers get German; every other locale the
 * menu's language switcher offers gets English (the app's fallback locale), or German if a note has
 * no English translation.
 */
function noteText(note: ReleaseNote): string {
    return activeLocale.value === "de" ? note.noteDe : note.noteEn || note.noteDe;
}

/** "2026-07" → a localized month heading, so no German month names come from the backend. */
function monthLabel(month: string): string {
    const [year, monthNumber] = month.split("-").map(Number);
    const date = new Date(year, (monthNumber ?? 1) - 1, 1);
    return date.toLocaleDateString(activeLocale.value, { month: "long", year: "numeric" });
}

const hasNotes = computed(() => months.value.some((m) => m.features.length + m.fixes.length > 0));

async function loadReleaseNotes() {
    loading.value = true;
    error.value = "";
    try {
        months.value = await ProfileAdministrationAPI.fetchPublicReleaseNotes();
        openPanels.value = Object.fromEntries(months.value.map((month) => [month.month, ["features"]]));
    } catch (err) {
        console.error("Failed to load release notes:", err);
        error.value = t("releaseNotes.error.loadingFailed");
    } finally {
        loading.value = false;
    }
}

// Watched rather than run on mount: the Unleash client resolves asynchronously, so the flag is
// still false for a moment after mount.
watch(enabled, async (isEnabled) => {
    if (!isEnabled) return;
    useLocaleFromQuery(route.query);
    await loadReleaseNotes();
}, { immediate: true });

// Redirect only once the client has really reported the flag as off — never on the initial
// "not fetched yet" false, which would bounce a legitimate visitor.
watch(flagsResolved, (resolved) => {
    if (resolved && !enabled.value) router.replace("/");
}, { immediate: true });
</script>

<template>
    <!-- The notes carry both languages, so the menu's language switch only re-renders, no refetch. -->
    <MainMenu highlight="news-release-notes" :trainer="trainer" />
    <div class="app-wrapper mb-8">
        <div id="main-content" tabindex="-1" class="mt-8 mb-6">
            <TitleHeadline
                :title="t('releaseNotes.heading')"
                :subtitle="t('releaseNotes.subHeading')"
                :subtitle-position="'bottom'"
                :top-margin="0"
            />
        </div>

        <div v-if="loading" class="flex justify-center p-6">
            <ProgressSpinner />
        </div>

        <Message v-else-if="error" severity="error" :closable="false" class="mb-6" data-cy="release-notes_error">
            {{ error }}
        </Message>

        <Message v-else-if="!hasNotes" severity="info" :closable="false" class="mb-6" data-cy="release-notes_empty">
            {{ t("releaseNotes.empty") }}
        </Message>

        <div v-else class="release-notes" data-cy="release-notes_list">
            <section v-for="month in months" :key="month.month" class="mb-8" :data-cy="`release-notes_month_${month.month}`">
                <h2 class="month-heading text-2xl font-semibold mb-4">{{ monthLabel(month.month) }}</h2>

                <!-- Both sections are collapsible; new features start open so the page leads with them. -->
                <Accordion v-model:value="openPanels[month.month]" multiple>
                    <AccordionPanel
                        v-if="month.features.length"
                        value="features"
                        :data-cy="`release-notes_features_${month.month}`"
                    >
                        <AccordionHeader>{{ t("releaseNotes.features", { count: month.features.length }) }}</AccordionHeader>
                        <AccordionContent>
                            <ul class="note-list" data-cy="release-notes_features">
                                <li v-for="note in month.features" :key="note.issueKey" class="mb-2">
                                    {{ noteText(note) }}
                                </li>
                            </ul>
                        </AccordionContent>
                    </AccordionPanel>

                    <AccordionPanel
                        v-if="month.fixes.length"
                        value="fixes"
                        :data-cy="`release-notes_fixes_${month.month}`"
                    >
                        <AccordionHeader>{{ t("releaseNotes.fixes", { count: month.fixes.length }) }}</AccordionHeader>
                        <AccordionContent>
                            <ul class="note-list" data-cy="release-notes_fixes">
                                <li v-for="note in month.fixes" :key="note.issueKey" class="mb-2">
                                    {{ noteText(note) }}
                                </li>
                            </ul>
                        </AccordionContent>
                    </AccordionPanel>
                </Accordion>
            </section>
        </div>
    </div>
    <Footer />
</template>

<style scoped>
.month-heading {
    border-bottom: 1px solid var(--p-surface-200);
    padding-bottom: 0.5rem;
}

/* Tailwind preflight strips list markers; restore bullets so each note reads as a list item. */
.note-list {
    padding-left: 1.25rem;
    list-style: disc;
    margin: 0;
}

/*
 * Same accordion look as the privacy page (persolog CI): orange header that stays orange when open
 * or hovered, white text and icon. Square corners come from the shared theme preset, so there is no
 * border-radius override here.
 */
.release-notes :deep(.p-accordion) {
    --p-accordion-header-active-background: var(--primary-color);
    --p-accordion-header-hover-background: var(--primary-color);
    --p-accordion-header-active-hover-background: var(--primary-color);
    --p-accordion-header-active-color: #ffffff;
    --p-accordion-header-hover-color: #ffffff;
    --p-accordion-header-active-hover-color: #ffffff;
    --p-accordion-header-toggle-icon-hover-color: #ffffff;
    --p-accordion-header-toggle-icon-active-color: #ffffff;
    --p-accordion-header-toggle-icon-active-hover-color: #ffffff;
    --p-accordion-content-padding: 1rem;
}
</style>
